import React, { useContext, useEffect, useMemo, useState } from 'react';
import BlockEditPanelControls from 'app/editor/report/content-editor/block-edit-panel/BlockEditPanelControls';
import { EditPanelContext } from 'app/editor/report/content-editor/block-edit-panel/BlockEditPanel';
import { AssocArray } from 'tools/types';
import { RawFormComponentType, FormDataAPIType } from 'components/common/form';
import ReactHookFormController from 'components/common/form/layout/ReactHookFormController';
import { alpha, Box, Button, Divider, IconButton, Skeleton, Stack } from '@mui/material';
import EditPanelCheckBox from 'app/editor/report/content-editor/block-edit-panel/edit-panel-components/EditPanelCheckBox';
import useBundleTranslation from 'i18n';
import useCustomSimplifiedForm from 'components/common/form/hooks/useCustomSimplifiedForm';
import { prepareFormElementProps } from 'components/common/form/formTools';
import BlockEditPanelHeader from 'app/editor/report/content-editor/block-edit-panel/BlockEditPanelHeader';
import IconMi from 'components/common/icon/IconMi';
import GetEmbedCode from 'app/editor/report/content-editor/block-edit-panel/edit-panel-components/GetEmbedCode';
import IncludeAsAttachment from 'app/editor/report/content-editor/block-edit-panel/edit-panel-components/IncludeAsAttachment';
import useHandleEditPanelError from 'components/report-content/hooks/useHandleEditPanelError';
import StaticLabelDivider from 'app/editor/report/content-editor/block-edit-panel/edit-panel-components/StaticLabelDivider';
import { FormComponentValue } from 'components/common/form/layout/control';
import validateKpiChart from './edit-panel/validateKpiChart';
import MeasureFieldsList from './edit-panel/MeasureFieldsList';
import MeasureFieldForm from './edit-panel/MeasureFieldForm';
import {
    ComponentSettingsKpiChart,
    DatasetField,
    BlockType,
    ComponentUpdateProps,
    ComponentSettingsChartDataQueries,
    ComponentSettingsLineChartAxis,
    getMeasureFieldOptionForFormUId,
    MeasureFieldSortingContext,
    useMeasureField,
    ComponentSettingsKpiChartMeasureField,
    ComponentSettingsChartMeasureField,
    IKpiAlertRule,
    MeasureField,
} from '@report-content';
import StoplightSection from './edit-panel/StoplightSection';
import KpiVarianceSection from './edit-panel/KpiVarianceSection';
import EditPanelLabel from 'app/editor/report/content-editor/block-edit-panel/edit-panel-components/EditPanelLabel';
import { useQuery } from '@tanstack/react-query';
import { instance } from 'api/api';
import AddAlertRulePopup from './edit-panel/AddAlertRulePopup';
import ReportAlertRuleWizard from 'app/report/report-alert-rule-wizard/ReportAlertRuleWizard';

// Field for form
const componentFormFields: Array<keyof ComponentSettingsKpiChart> = [
    'title',
    'show_in_viewer',
    'show_summary_in_viewer',
    'include_in_email',
    'include_in_email_attachment',
];
const componentNumericFormField: Array<keyof ComponentSettingsKpiChart> = ['height'];

function useAlertRules(elementId: number, settingsAlertRules: IKpiAlertRule[]) {
    // All existing Alert Rules for KPI
    const [alerts, setAlerts] = useState<Array<IKpiAlertRule>>([]);
    const { data, isLoading, isFetching, refetch } = useQuery<Array<IKpiAlertRule>, Error>({
        queryKey: ['alerts_for_kpi_to_add', elementId],
        queryFn: async () => {
            const response = await instance.get(`/data/editor/report/${elementId}/alert-rule-for-kpi-for-add`);
            return response.data.status == 'OK' ? response.data.data : [];
        },
        refetchOnMount: true,
    });
    useEffect(() => {
        setAlerts(data ?? []);
    }, [data]);

    // Current selected Alert Rules for KPI
    // May contain rules that are not existing anymore
    // To avoid rendering of removed rules will filter them and store to addedAlertRules
    const [currentAlertRules, setCurrentAlertRules] = useState<Array<IKpiAlertRule>>(settingsAlertRules);
    // Filtered list of Alert Rules that are currently added to KPI and exist in system
    const addedAlertRules = useMemo(() => {
        return isLoading
            ? []
            : alerts.filter((ar) => currentAlertRules.some((a) => a.report_alert_rule_id == ar.report_alert_rule_id));
    }, [currentAlertRules, alerts, isLoading]);

    return { alerts, isLoading, isFetching, refetch, currentAlertRules, setCurrentAlertRules, addedAlertRules };
}

export default function KpiChartEditPanel({
    component,
    block,
    contentSettings,
}: ComponentUpdateProps<ComponentSettingsKpiChart> & { block: BlockType<ComponentSettingsKpiChart> }) {
    const { t } = useBundleTranslation(['components/report-content']);
    const editPanelContext = useContext(EditPanelContext);
    const { editPanel, setErrorMessage, errorBlock } = useHandleEditPanelError();
    const datasetFields = editPanelContext?.datasetFields ?? [];
    const hasSnapshot = contentSettings.instances.length > 0;

    // Apply Button Click save all changes to ReportState
    const handleApply = () => {
        // Process measure fields settings
        const fields = measureFields.map((field) => {
            return {
                ...field,
                color: elementProps.form.hookFormGetValues(getMeasureFieldOptionForFormUId(field, 'color')),
                aggregation_function: elementProps.form.hookFormGetValues(
                    getMeasureFieldOptionForFormUId(field, 'aggregation_function'),
                ),
                is_primary: elementProps.form.hookFormGetValues(getMeasureFieldOptionForFormUId(field, 'is_primary')),
                overlay_in_chart: elementProps.form.hookFormGetValues(
                    getMeasureFieldOptionForFormUId(field, 'overlay_in_chart'),
                ),
                overlay_in_tile: elementProps.form.hookFormGetValues(
                    getMeasureFieldOptionForFormUId(field, 'overlay_in_tile'),
                ),
                display_type: elementProps.form.hookFormGetValues(
                    getMeasureFieldOptionForFormUId(field, 'display_type'),
                ),
            };
        });

        const stoplightSettings: any = {};
        Object.keys(component.settings.stoplight).forEach((k: string) => {
            stoplightSettings[k] = elementProps.form.hookFormGetValues(k);
        });
        stoplightSettings.stoplightTarget = stoplightTarget;

        const kpiVarianceSettings: any = {};
        Object.keys(component.settings.kpiVariance).forEach((k: string) => {
            kpiVarianceSettings[k] = elementProps.form.hookFormGetValues(k);
        });
        kpiVarianceSettings.varianceCalcMethod =
            elementProps.form.hookFormGetValues('varianceCalcMethod') ?? 'prior values';
        kpiVarianceSettings.varianceTarget = varianceTarget;

        const newSettings: ComponentSettingsKpiChart = {
            ...component.settings,
            x_axis: { ...xAxis, title: elementProps.form.hookFormGetValues('xAxisTitle') },
            y_axis: yAxis,
            measureFields: fields,
            stoplight: stoplightSettings,
            kpiVariance: kpiVarianceSettings,
            alertRules: currentAlertRules,
        } as ComponentSettingsKpiChart;

        //@ts-ignore
        componentFormFields.forEach((f) => (newSettings[f] = elementProps.form.hookFormGetValues(f)));
        //@ts-ignore
        componentNumericFormField.forEach((f) => (newSettings[f] = Number(elementProps.form.hookFormGetValues(f))));

        let dataQueries: ComponentSettingsChartDataQueries = {
            table: {
                resultFields: [newSettings.x_axis.field],
                resultFieldAggregations: {},
                sort: [{ field: newSettings.x_axis.field, dir: 'ASC' }],
            },
        };

        newSettings.queryAgainstEntireDataset = newSettings.x_axis.field == 'measurement_time' ? 'Y' : 'N';

        newSettings.measureFields.forEach((field) => {
            dataQueries['table'].resultFields.push(field.reference_name);
            dataQueries['table'].resultFieldAggregations[field.reference_name] = field.aggregation_function;
        });

        const resultSettings = {
            ...newSettings,
            dataQueries: dataQueries,
        } as ComponentSettingsKpiChart;

        const validationMsg = validateKpiChart(resultSettings, t);
        setErrorMessage(validationMsg);
        if (!validationMsg) {
            editPanelContext?.updateBlockSettings(block.uid, resultSettings, true);
        }
    };
    const getInitialXAxis = () => {
        let xAxis = component.settings.x_axis;
        if (xAxis.field == '') {
            xAxis.defaultZoom = 'all';

            if (hasSnapshot) {
                xAxis.field = 'measurement_time';
                xAxis.value_type = 'datetime';
                xAxis.display_mask_id = '0';
                return xAxis;
            }

            let field = datasetFields.find((f) => f.value_type != 'numeric');
            if (!field) {
                field = datasetFields[0];
            }
            xAxis.field = field.reference_name;
            xAxis.value_type = field.value_type;
            xAxis.display_mask_id = field.display_mask_id ?? '0';
        }
        return xAxis;
    };

    const {
        alerts: allowedAlertRules,
        isLoading: alertRulesIsLoading,
        isFetching: alertRulesIsFetching,
        refetch: refetchAlertRules,
        currentAlertRules,
        setCurrentAlertRules,
        addedAlertRules,
    } = useAlertRules(contentSettings.elementId, component.settings.alertRules);

    const {
        measureFields,
        selectedMeasure,
        setSelectedMeasure,
        handleSelectMeasureField,
        handleRemoveMeasureField,
        handleMeasureFieldUpdate,
        handleMeasureFieldsSort,
        handleAddMeasureField,
    } = useMeasureField<ComponentSettingsKpiChartMeasureField>(
        datasetFields,
        component.settings.measureFields,
        contentSettings,
        'kpi',
    );

    // No smart default here: an unselected target must stay unselected (shown as "-") rather than
    // silently comparing a measure to itself, so getDefaultTarget only recalls what was saved.
    const getDefaultTarget = (savedTarget: MeasureField | null): MeasureField =>
        savedTarget ?? { reference_name: '', aggregation_function: '', value_type: 'text', column_name: '' };

    const [stoplightTarget, setStoplightTarget] = useState<MeasureField>(
        getDefaultTarget(component.settings.stoplight.stoplightTarget),
    );

    const [varianceTarget, setVarianceTarget] = useState<MeasureField>(
        getDefaultTarget(component.settings.kpiVariance.varianceTarget),
    );

    const [xAxis, setXAxis] = useState<ComponentSettingsLineChartAxis>(getInitialXAxis());
    const [yAxis, setYAxis] = useState<Array<ComponentSettingsLineChartAxis>>(component.settings.y_axis ?? []);
    // Prepare data for Form
    const defaultState: AssocArray<any> = {
        ...component.settings.stoplight,
        ...component.settings.kpiVariance,
        xAxisField: xAxis.field,
        xAxisTitle: xAxis.title,
        xAxisFieldZoom: xAxis.defaultZoom ?? '',
    };
    // Add simple fields to form
    componentFormFields.forEach((f) => (defaultState[f] = component.settings[f]));
    // Set Default for new Settings Property
    defaultState.show_summary_in_viewer = component.settings.show_summary_in_viewer ?? 'Y';
    defaultState.varianceCalcMethod = component.settings.kpiVariance.varianceCalcMethod ?? 'prior values';
    componentNumericFormField.forEach((f) => (defaultState[f] = Number(component.settings[f])));
    measureFields.forEach((field) => {
        defaultState[getMeasureFieldOptionForFormUId(field, 'color')] = field.color;
        defaultState[getMeasureFieldOptionForFormUId(field, 'aggregation_function')] = field.aggregation_function;
        defaultState[getMeasureFieldOptionForFormUId(field, 'is_primary')] = field.is_primary;
        defaultState[getMeasureFieldOptionForFormUId(field, 'overlay_in_chart')] = field.overlay_in_chart;
        defaultState[getMeasureFieldOptionForFormUId(field, 'overlay_in_tile')] = field.overlay_in_tile;
        defaultState[getMeasureFieldOptionForFormUId(field, 'display_type')] = field.display_type;
    });
    const elementProps: FormDataAPIType = useCustomSimplifiedForm(defaultState);

    // xAxis settings
    const xAxisFieldName = elementProps.form.hookFormWatch('xAxisField');
    useEffect(() => {
        let field = datasetFields.find((f) => f.reference_name_escaped == xAxisFieldName);
        if (xAxisFieldName == 'measurement_time') {
            field = { value_type: 'datetime', display_mask_id: '' } as DatasetField;
        }
        setXAxis((prev) => {
            if (!field) {
                return prev;
            }
            return {
                ...prev,
                field: xAxisFieldName,
                value_type: field.value_type,
                display_mask_id: field.display_mask_id ?? '',
            };
        });
    }, [xAxisFieldName]);

    const xAxisList = useMemo(() => {
        const emptyOption: FormComponentValue = { label: '--', value: '' };
        let result: FormComponentValue[] = [];
        if (hasSnapshot) {
            result.push({
                label: 'Snapshot Date',
                value: 'measurement_time',
            });
        }
        result = result.concat(
            datasetFields
                .filter((f) => f.value_type == 'datetime')
                .map((f) => ({
                    label: f.column_name,
                    value: f.reference_name_escaped,
                })),
        );
        return result.length == 0 ? [emptyOption] : result;
    }, [datasetFields, hasSnapshot]);

    const actualHeight = elementProps.form.hookFormWatch('height');
    const showInViewer = elementProps.form.hookFormWatch('show_in_viewer') == 'Y';
    const [addAlertPopupIsOpen, setAddAlertPopupIsOpen] = useState(false);
    const [editingAlertRuleId, setEditingAlertRuleId] = useState<number | null>(null);
    const handleConfirmAddAlertRules = (newAlertRules: number[]) => {
        const addedRules = allowedAlertRules.filter((r) => newAlertRules.includes(r.report_alert_rule_id));
        setCurrentAlertRules(addedRules);
    };

    const removeAlertRule = (ruleId: number) => {
        const updatedRules = currentAlertRules.filter((r) => r.report_alert_rule_id !== ruleId);
        setCurrentAlertRules(updatedRules);
    };
    return (
        <>
            {/* Kept mounted and hidden via CSS (rather than unmounted) so returning from the measure
                edit form doesn't pay the cost of rebuilding this whole panel */}
            <Stack height={1} sx={{ display: selectedMeasure == null ? 'flex' : 'none' }}>
                {/*Main Panel*/}
                <Box flexShrink={0}>
                    <BlockEditPanelHeader
                        title={t('kpi_chart.edit_title')}
                        onCancel={() => editPanelContext?.closeEditPanel(block.uid, component.settings)}
                    />
                </Box>
                <Stack ref={editPanel} sx={{ overflow: 'auto', flexGrow: 1, p: 3 }} spacing={2}>
                    {errorBlock}
                    <Box>
                        <ReactHookFormController
                            elementProps={prepareFormElementProps({
                                ...elementProps,
                                component: { component: 'FormText', name: 'title', label: t('title') },
                            })}
                        />
                    </Box>
                    <Box>
                        <EditPanelLabel>{t('kpi_chart.date_column')}</EditPanelLabel>
                        <ReactHookFormController
                            componentValues={xAxisList}
                            elementProps={prepareFormElementProps({
                                ...elementProps,
                                ...{
                                    component: {
                                        component: 'FormSelect',
                                        name: 'xAxisField',
                                    } as RawFormComponentType,
                                },
                            })}
                        />
                    </Box>
                    {/*MeasureFields Sort Section*/}
                    <Divider />
                    <StaticLabelDivider>{t('measure')}</StaticLabelDivider>
                    <Box>
                        <MeasureFieldSortingContext.Provider
                            value={{
                                elementProps: elementProps,
                                selectField: handleSelectMeasureField as <T extends ComponentSettingsChartMeasureField>(
                                    field: T,
                                ) => void,
                                removeField: handleRemoveMeasureField as <T extends ComponentSettingsChartMeasureField>(
                                    field: T,
                                ) => void,
                                onUpdateMeasureField: handleMeasureFieldUpdate as <
                                    T extends ComponentSettingsChartMeasureField,
                                >(
                                    field: T,
                                ) => void,
                                onSortEnd: handleMeasureFieldsSort,
                                datasetFields: datasetFields,
                            }}
                        >
                            <MeasureFieldsList fields={measureFields} />
                        </MeasureFieldSortingContext.Provider>
                        <Box sx={{ mt: 2 }}>
                            <Button
                                onClick={() => handleAddMeasureField(elementProps.form)}
                                variant={'light'}
                                startIcon={<IconMi icon={'new'} />}
                            >
                                {t('measure')}
                            </Button>
                        </Box>
                    </Box>
                    {/*Alerts Section*/}
                    <Divider />
                    <StaticLabelDivider>{t('kpi_chart.displayed_alert_rules')}</StaticLabelDivider>
                    <Stack>
                        {alertRulesIsLoading ? (
                            <Stack spacing={1}>
                                <Skeleton variant="rectangular" height={32} />
                                <Skeleton variant="rectangular" height={64} />
                            </Stack>
                        ) : (
                            <>
                                <Box
                                    sx={{
                                        color: (theme) => alpha(theme.palette.text.primary, 0.8),
                                        backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.08),
                                        px: 1,
                                        py: '7px',
                                    }}
                                >
                                    {t('kpi_chart.alert_name')}
                                </Box>
                                {addedAlertRules.map((rule) => (
                                    <Stack direction={'row'} alignItems={'center'} key={rule.report_alert_rule_id}>
                                        <Box width={'100%'} sx={{ pl: 1 }}>
                                            {rule.name}
                                        </Box>
                                        <Box flexShrink={0}>
                                            <IconButton
                                                onClick={() => setEditingAlertRuleId(rule.report_alert_rule_id)}
                                                data-test={`alert-edit-${rule.report_alert_rule_id}`}
                                            >
                                                <IconMi icon="edit-pencil" fontSize="16" />
                                            </IconButton>
                                            <IconButton
                                                onClick={() => removeAlertRule(rule.report_alert_rule_id)}
                                                data-test={`alert-remove-${rule.report_alert_rule_id}`}
                                            >
                                                <IconMi icon="times" fontSize="16" />
                                            </IconButton>
                                        </Box>
                                    </Stack>
                                ))}
                                {editingAlertRuleId != null && (
                                    <ReportAlertRuleWizard
                                        elementId={contentSettings.elementId}
                                        alertRuleId={editingAlertRuleId}
                                        isOpen={true}
                                        setIsOpen={(open) => {
                                            if (!open) {
                                                setEditingAlertRuleId(null);
                                                refetchAlertRules();
                                            }
                                        }}
                                    />
                                )}
                                <Box sx={{ mt: 2 }}>
                                    <Button
                                        onClick={() => setAddAlertPopupIsOpen(true)}
                                        variant={'light'}
                                        startIcon={<IconMi icon={'new'} />}
                                    >
                                        {t('kpi_chart.alert_rule')}
                                    </Button>
                                </Box>
                                <AddAlertRulePopup
                                    elementId={contentSettings.elementId}
                                    allowedAlertRules={allowedAlertRules}
                                    addedAlertRules={addedAlertRules}
                                    isOpen={addAlertPopupIsOpen}
                                    setIsOpen={setAddAlertPopupIsOpen}
                                    onConfirm={handleConfirmAddAlertRules}
                                    onNewAlertRuleCreated={refetchAlertRules}
                                    alertRulesIsFetching={alertRulesIsFetching}
                                />
                            </>
                        )}
                    </Stack>

                    <Divider />
                    <StoplightSection
                        stoplightTarget={stoplightTarget}
                        onTargetUpdate={(target: MeasureField) => setStoplightTarget(target)}
                        elementProps={elementProps}
                        elementId={contentSettings.elementId}
                        fieldsList={datasetFields}
                    />
                    <Divider />
                    <KpiVarianceSection
                        varianceTarget={varianceTarget}
                        onTargetUpdate={(target: MeasureField) => setVarianceTarget(target)}
                        elementProps={elementProps}
                        fieldsList={datasetFields}
                    />
                    <Divider />

                    <Stack alignItems={'center'} direction={'row'} spacing={1}>
                        <Box sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>Height (px) </Box>
                        <Box sx={{ width: '72px' }}>
                            <ReactHookFormController
                                elementProps={prepareFormElementProps({
                                    ...elementProps,
                                    inputFilter: 'int',
                                    component: { component: 'FormText', name: 'height', label: '' },
                                })}
                            />
                        </Box>
                    </Stack>
                    <Divider />
                    <Box>
                        <EditPanelCheckBox
                            elementProps={elementProps}
                            name={'show_in_viewer'}
                            label={t('show_in_viewer')}
                        />
                    </Box>
                    <Box className={!showInViewer ? 'd-none' : ''}>
                        <EditPanelCheckBox
                            elementProps={elementProps}
                            name={'show_summary_in_viewer'}
                            label={t('kpi_chart.show_summary_in_viewer')}
                        />
                    </Box>
                    <Box>
                        <EditPanelCheckBox
                            elementProps={elementProps}
                            name={'include_in_email'}
                            label={t('include_in_email')}
                        />
                    </Box>
                    <IncludeAsAttachment componentName={component.internalName} elementProps={elementProps} t={t} />
                    <Box>
                        <GetEmbedCode
                            contentSettings={contentSettings}
                            height={actualHeight}
                            blockUID={block.uid}
                            t={t}
                        />
                    </Box>
                </Stack>
                <Box flexShrink={0}>
                    <BlockEditPanelControls
                        onApply={handleApply}
                        onCancel={() => editPanelContext?.closeEditPanel(block.uid, component.settings)}
                    />
                </Box>
            </Stack>
            {selectedMeasure != null && (
                <MeasureFieldForm
                    field={selectedMeasure}
                    onApply={handleMeasureFieldUpdate}
                    onCancel={() => setSelectedMeasure(null)}
                    title={t('kpi_chart.edit_title')}
                />
            )}
        </>
    );
}
